import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';

/**
 * Admin panel: match queue status and management.
 */
export default function AdminQueue({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Clear state
  const [clearLoading, setClearLoading] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const fetchQueue = useCallback(async () => {
    if (!perms.guildId) return;
    try {
      const r = await fetch(`${API_URL}/api/queue/${perms.guildId}`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        setQueue(await r.json());
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [perms.guildId, getAuthHeaders]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleKick = async (userId) => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/queue/${perms.guildId}/kick`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!r.ok) {
        const data = await r.json();
        setError(data.error || data.detail || t('admin.kickFailed'));
      } else {
        fetchQueue();
      }
    } catch {
      setError(t('settings.networkError'));
    }
  };

  const handleClear = async () => {
    setShowClearConfirm(false);
    setClearLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/queue/${perms.guildId}/clear`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      if (!r.ok) {
        const data = await r.json();
        setError(data.error || data.detail || t('admin.clearFailed'));
      } else {
        fetchQueue();
      }
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setClearLoading(false);
    }
  };

  const players = queue?.players || [];

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.navQueue')}</h1>

      {error && <div className="admin-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="admin-card">
        <div className="admin-card-title">{t('admin.queueStatus')}</div>
        {loading ? (
          <div className="admin-info">{t('live.loading')}</div>
        ) : (
          <>
            <div className="admin-stat-grid">
              <div className="admin-stat-card">
                <div className="admin-stat-label">{t('admin.playersInQueue')}</div>
                <div className="admin-stat-value">{players.length}</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-label">{t('admin.colStatus')}</div>
                <div className="admin-stat-value">{queue?.locked ? t('admin.locked') : t('admin.open')}</div>
              </div>
            </div>

            {players.length > 0 && (
              <table className="admin-table" style={{ marginTop: '0.75rem' }}>
                <thead>
                  <tr>
                    <th>{t('nav.players')}</th>
                    <th>ID</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p) => (
                    <tr key={p.user_id || p.id}>
                      <td>{p.username || p.name || '-'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.user_id || p.id}</td>
                      <td>
                        {perms.hasCommand('queue.kick') && (
                          <button
                            className="admin-btn admin-btn-danger"
                            style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                            onClick={() => handleKick(p.user_id || p.id)}
                          >
                            {t('admin.kick')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {perms.hasAnyCommand('queue.clear') && players.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <button
                  className="admin-btn admin-btn-danger"
                  onClick={() => setShowClearConfirm(true)}
                  disabled={clearLoading}
                >
                  {clearLoading ? t('admin.clearing') : t('admin.clearQueue')}
                </button>
              </div>
            )}

          </>
        )}
      </div>

      {showClearConfirm && (
        <div className="admin-confirm-overlay" onClick={() => setShowClearConfirm(false)}>
          <div className="admin-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>{t('admin.clearQueueConfirm')}</h3>
            <p>
              {t('admin.clearQueueText', { count: players.length })}
            </p>
            <div className="admin-confirm-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setShowClearConfirm(false)}>
                {t('matchDetail.cancel')}
              </button>
              <button className="admin-btn admin-btn-danger" onClick={handleClear}>
                {t('admin.yesClear')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
